import React, { useState, useEffect } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import { Formik } from "formik";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Box,
  CircularProgress,
  InputAdornment as MuiInputAdonment,
  Typography,
  Alert,
  TextField,
  Button,
  IconButton,
  Paper,
} from "@mui/material";
import { Eye, EyeOff } from "react-feather";
import { TextLink } from "./styles";
import API from "../../../axios/instances/API";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: fit-content;
`;

const Card = styled(Paper)`
  padding: 30px 40px 40px 40px;
  width: calc(min(520px, 96vw));
  min-height: 140px;

  @media (max-width: 560px) {
    padding: 20px;

    h2 {
      font-size: 22px;
    }
  }
`;

const InputAdornment = styled(MuiInputAdonment)`
  cursor: pointer;
`;

const initialValues = {
  newPassword: "",
  confirmPassword: "",
};

const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("Password is required")
    .max(255, "Password maximum 255 characters")
    .min(8, "Password minimum 8 characters"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords are not the same")
    .required("Password confirm is required"),
});

const ResetPassword = ({ match }) => {
  const [showAlert, setShowAlert] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetToken, setResetToken] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAndAssignToken = async () => {
      try {
        const { token } = match.params;
        await API.post("/dashboard/auth/verify-password-token/", {
          token,
        });
        setResetToken(token);
        setIsLoading(false);
      } catch (error) {
        setTokenExpired(true);
        setIsLoading(false);
      }
    };
    checkAndAssignToken();
  }, []);

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      await API.post("dashboard/auth/reset-password/", {
        password1: values.newPassword.trim(),
        password2: values.confirmPassword.trim(),
        token: resetToken,
      });
      setShowAlert(true);
      setSubmitting(false);
      resetForm();
    } catch (error) {
      setShowError(true);
      setSubmitting(false);
      resetForm();
      setShowPassword(false);
    }
  };

  if (isLoading) {
    return (
      <Wrapper>
        <CircularProgress size={30} />
      </Wrapper>
    );
  }

  if (tokenExpired) {
    return (
      <Wrapper>
        <Card align="center">
          <Typography variant="h4">
            Password Reset has timed out.. try again
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Button
              component={Link}
              to="/recover-password"
              variant="contained"
              color="primary"
              fullWidth
            >
              Go Back To Password Reset
            </Button>
          </Box>
        </Card>
      </Wrapper>
    );
  }

  return (
    <>
      <Helmet title="Spiffy | Reset Password" />
      <Wrapper>
        <Card>
          <Typography variant="h2" component="h2" align="center">
            Reset Your Password
          </Typography>
          <Typography
            component="h5"
            variant="subtitle2"
            align="center"
            color="textSecondary"
            sx={{ mb: 4 }}
          >
            Enter your new Password
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
            }) => (
              <form noValidate onSubmit={handleSubmit}>
                <TextField
                  type={showPassword ? "text" : "password"}
                  name="newPassword"
                  size="small"
                  label="New Password"
                  value={values.newPassword}
                  error={Boolean(touched.newPassword && errors.newPassword)}
                  fullWidth
                  helperText={touched.newPassword && errors.newPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  autoComplete="off"
                  sx={{ mb: 4 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((p) => !p)}>
                          {showPassword ? (
                            <Eye height={13} width={13} />
                          ) : (
                            <EyeOff height={13} width={13} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  type="password"
                  name="confirmPassword"
                  label="Confirm Password"
                  size="small"
                  value={values.confirmPassword}
                  error={Boolean(
                    touched.confirmPassword && errors.confirmPassword,
                  )}
                  fullWidth
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  autoComplete="off"
                />
                <Box sx={{ mt: 6 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={20} />}
                  >
                    {isSubmitting ? "Resetting" : "Reset Password"}
                  </Button>
                </Box>
                {showAlert && (
                  <Alert
                    sx={{ mt: 2 }}
                    severity="success"
                    onClose={() => setShowAlert(false)}
                  >
                    New Password Saved!{" "}
                    <TextLink color="inherit" component={Link} to="/">
                      Log In
                    </TextLink>
                  </Alert>
                )}
                {showError && (
                  <Alert
                    sx={{ mt: 2 }}
                    severity="error"
                    onClose={() => setShowError(false)}
                  >
                    Hmm.. something went wrong
                  </Alert>
                )}
              </form>
            )}
          </Formik>
        </Card>
      </Wrapper>
    </>
  );
};

export default ResetPassword;
